import { fmtEur, promotions, type PromoStatus } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

const columns: { key: PromoStatus; label: string }[] = [
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Submitted" },
  { key: "review", label: "Under review" },
  { key: "approved", label: "Approved" },
  { key: "live", label: "Live" },
  { key: "rejected", label: "Rejected" },
];

export function renderPromotions(ctx: Ctx) {
  const live = promotions.filter((p) => p.status === "live");
  const totalRedemptions = live.reduce(
    (a, p) => a + (p.redemptions ?? 0),
    0,
  );
  const totalBudget = promotions.reduce((a, p) => a + p.budget, 0);
  const totalUsed = promotions.reduce((a, p) => a + p.budgetUsed, 0);
  const top = live[0];
  const topUsedPct =
    top && top.budget > 0 ? Math.min(100, (top.budgetUsed / top.budget) * 100) : 0;

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Growth<span class="page-eyebrow-rule"></span>Promotions</span>
        <h1 class="page-title">Drive customer engagement</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">Templates</button>
        <button class="btn btn-primary">${icon("plus", 11)} New promotion</button>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Live campaigns</span>
        <span class="summary-cell-value">${live.length}</span>
        <span class="summary-cell-note">In marketplace right now</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Redemptions · 7d</span>
        <span class="summary-cell-value">${totalRedemptions.toLocaleString("en-EU")}</span>
        <span class="summary-cell-note is-up">↑ 22% vs prev</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Budget allocated</span>
        <span class="summary-cell-value">${fmtEur(totalBudget, { compact: true })}</span>
        <span class="summary-cell-note">Across ${promotions.length} campaigns</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Spend · 30d</span>
        <span class="summary-cell-value">${fmtEur(totalUsed, { compact: true })}</span>
        <span class="summary-cell-note">${((totalUsed / totalBudget) * 100).toFixed(0)}% of allocated</span>
      </div>
    </section>

    ${
      top
        ? `<section class="panel panel-feature top-perf">
      <div class="top-perf-watermark" aria-hidden="true"></div>
      <div class="top-perf-inner">
        <div class="top-perf-meta">
          <div class="panel-eyebrow">Top performer</div>
          <h2 class="top-perf-title">${top.title}</h2>
          <p class="top-perf-sub">${top.reward} · valid ${top.startDate} → ${top.endDate} · ${top.audience}</p>
        </div>

        <div class="top-perf-stats">
          <div class="top-perf-stat">
            <span class="top-perf-stat-label">Redemptions</span>
            <span class="top-perf-stat-value">${(top.redemptions ?? 0).toLocaleString("en-EU")}</span>
          </div>
          <div class="top-perf-stat">
            <span class="top-perf-stat-label">Avg uplift</span>
            <span class="top-perf-stat-value">+${top.uplift ?? 0}<small>%</small></span>
          </div>
          <div class="top-perf-stat">
            <span class="top-perf-stat-label">Budget used</span>
            <span class="top-perf-stat-value">${fmtEur(top.budgetUsed, { compact: true })}<small> / ${fmtEur(top.budget, { compact: true })}</small></span>
            <div class="top-perf-bar">
              <div class="top-perf-bar-fill" style="right: ${100 - topUsedPct}%;"></div>
            </div>
          </div>
        </div>

        <div class="top-perf-actions">
          <button class="btn btn-ghost btn-sm">Open analytics</button>
          <button class="btn btn-primary btn-sm">${icon("plus", 11)} New from template</button>
        </div>
      </div>
    </section>`
        : ""
    }

    <section class="panel">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">Pipeline</div>
          <h2 class="panel-title">All promotions, by status</h2>
          <p class="panel-sub">Click a card to open the promotion review thread.</p>
        </div>
        <div class="pill-toggle">
          <button class="is-active">Board</button>
          <button>List</button>
          <button>Calendar</button>
        </div>
      </div>

      <div class="board">
        ${columns
          .map((c) => {
            const cards = promotions.filter((p) => p.status === c.key);
            return `<div class="board-col">
              <div class="board-col-head">
                <span class="board-col-title">${c.label}</span>
                <span class="board-col-count">${cards.length}</span>
              </div>
              ${cards.length === 0
                ? `<div class="board-col-empty">No promotions in this stage</div>`
                : cards
                    .map(
                      (p) => `<div class="board-card">
                <span class="promo-status is-${p.status}">${p.statusLabel}</span>
                <div class="board-card-title">${p.title}</div>
                <div class="board-card-meta">
                  ${p.reward}<br>
                  ${p.audience}
                </div>
                ${
                  p.budget > 0
                    ? `<div class="board-card-foot">
                  <span><span class="board-card-foot-label">Budget</span><br><strong>${fmtEur(p.budget, { compact: true })}</strong></span>
                  <span style="text-align: right;">${
                    p.status === "live"
                      ? `<span class="board-card-foot-label">Redeemed</span><br><strong>${(p.redemptions ?? 0).toLocaleString("en-EU")}</strong>`
                      : p.status === "rejected"
                        ? `<span class="board-card-foot-label">Rev. by</span><br><strong>D. Kruse</strong>`
                        : `<span class="board-card-foot-label">Used</span><br><strong>${fmtEur(p.budgetUsed, { compact: true })}</strong>`
                  }</span>
                </div>`
                    : ""
                }
              </div>`,
                    )
                    .join("")}
            </div>`;
          })
          .join("")}
      </div>
    </section>
  `;

  // Wire pill-toggle (visual only — list/calendar views are out-of-scope for this mockup)
  ctx.canvas
    .querySelectorAll<HTMLButtonElement>(".pill-toggle button")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const group = btn.parentElement!;
        group
          .querySelectorAll(".is-active")
          .forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
      });
    });

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });
}
